// src/pages/Stops.jsx
import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Button, Modal, Row, Col, Form } from 'react-bootstrap';
import { Tooltip } from '@mui/material';
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import loading from '../../assets/images/common/loading.gif';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AppDataGrid from '../../components/common/AppDataGrid';

const Stops = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [Stops, setStops] = useState([]);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData || '{}');

  const [ShowAddressModal, setShowAddressModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');

  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);
  const handleSearchChange = (event) => setSearchQuery(event.target.value);

  const titleCase = (s = '') =>
    String(s).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

  const columns = [
    {
      field: 'route_name',
      headerName: 'Route Name',
      flex: 1,
      renderCell: (p) => <Tooltip title={titleCase(p.value)}><span>{titleCase(p.value)}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'stop_name',
      headerName: 'Stop Name',
      flex: 1,
      renderCell: (p) => <Tooltip title={titleCase(p.value)}><span>{titleCase(p.value)}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'stop_sequence',
      headerName: 'Stop Sequence',
      flex: 1,
      renderCell: (p) => <Tooltip title={`${p.value}`}><span>{p.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'pickup_time',
      headerName: 'Pickup Time',
      flex: 1,
      renderCell: (p) => <Tooltip title={`${p.value}`}><span>{p.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'drop_time',
      headerName: 'Drop Time',
      flex: 1,
      renderCell: (p) => <Tooltip title={`${p.value}`}><span>{p.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'latitude',
      headerName: 'Latitude',
      flex: 1,
      renderCell: (p) => <Tooltip title={`${p.value}`}><span>{p.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'longitude',
      headerName: 'Longitude',
      flex: 1,
      renderCell: (p) => <Tooltip title={`${p.value}`}><span>{p.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      renderCell: (p) => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setSelectedAddress(p.value);
            setShowAddressModal(true);
          }}
        >
          {(p.value ?? '').length > 15 ? `${p.value.substring(0, 15)}...` : p.value}
        </div>
      ),
      sortable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <div className="tableActions">
          <Tooltip title="Edit">
            <span className="commonActionIcons" onClick={() => handleEditClick(params.row.stop_id)}>
              <MdEdit />
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span className="commonActionIcons" onClick={() => handleDeleteClick(params.row.stop_id)}>
              <MdDelete />
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleEditClick = (stop_id) => {
    const stopsMasterToEdit = Stops.find((stop) => stop.stop_id === stop_id);
    if (stopsMasterToEdit) navigate('/addstops', { state: { routesData: stopsMasterToEdit } });
  };

  const handleDeleteClick = async (stop_id) => {
    const ok = window.confirm('Are you sure you want to delete/deactivate this stop?');
    if (!ok) return;

    try {
      setIsLoading(true);
      const action = 'DEACTIVATE';
      const { data } = await axios.post(
        `${baseUrl}/stops`,
        { stop_id, school_id: userObj.school_id, action },
        { headers: { 'Content-Type': 'application/json' } }
      );

      toast.success(data?.message || 'Record Deleted Successfully');
      await fetchData();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Delete failed';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const toStr = (v) => (v ?? '').toString();
  const lower = (s) => s.toLowerCase();
  const stripNonAlnum = (s) => s.replace(/[^a-z0-9]/gi, ''); 

  const qRaw = searchQuery.trim();
  const q1 = lower(qRaw);         
  const q2 = stripNonAlnum(q1);   

  const fieldMatches = (value) => {
    const v = toStr(value);
    const v1 = lower(v);
    const v2 = stripNonAlnum(v1);
    return (q1 && v1.includes(q1)) || (q2 && v2.includes(q2));
  };

  const rows = (Stops || []).filter((r) => {
    if (!q1 && !q2) return true; 
    const candidates = [
      r.route_name,
      r.stop_name,
      r.stop_sequence,
      r.pickup_time,
      r.drop_time,
      r.latitude,
      r.longitude,
      r.address,
    ];
    return candidates.some(fieldMatches);
  });

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/stops/${userObj.school_id}`, { params: { action: 'READ' } });
      setStops(Array.isArray(data) ? data : []);
    } catch {
      setStops([]);
    }
  };

  const [form, setForm] = useState({
    stop_name: '',
    route_name: '',
    action: 'FILTER',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prevForm) => ({ ...prevForm, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.get(`${baseUrl}/stops/${userObj.school_id}`, {
        params: {
          action: 'FILTER',
          route_name: form.route_name || null,
          stop_name: form.stop_name || null
        },
      });
      setStops(Array.isArray(data) ? data : []);
      setShowFilterModal(false);
    } catch {
      setStops([]);
      setShowFilterModal(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setForm({ route_name: '', stop_name: '', action: 'FILTER' });
    setShowFilterModal(true);
    fetchData();
  };

  return (
    <div className="pageBody">
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: '10px' }}>
            <h6 className="commonTableTitle">Stops</h6>
          </div>
          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
              <Button className="primaryBtn" variant="primary" onClick={() => navigate('/addstops')}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => r.stop_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Row>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Stop Name</Form.Label>
                  <Form.Control
                    type="text"
                    id="stop_name"
                    value={form.stop_name}
                    placeholder="Enter Stop Name"
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === ' ' && e.target.selectionStart === 0) e.preventDefault();
                    }}
                    maxLength={30}
                  />
                </Form.Group>
              </div>
            </Col>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Route Name</Form.Label>
                  <Form.Control
                    type="text"
                    id="route_name"
                    value={form.route_name}
                    placeholder="Enter Route Name"
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === ' ' && e.target.selectionStart === 0) e.preventDefault();
                    }}
                    maxLength={30}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleSubmit}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={ShowAddressModal} onHide={() => setShowAddressModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Address</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          <p>{selectedAddress}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddressModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Stops;
